import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import PropertyImage from '../../components/PropertyImage';
import { isTenantVisibleProperty } from '../../utils/propertyLifecycle';
import { 
  Star, 
  MapPin, 
  Euro, 
  Bed, 
  Home as HomeIcon,
  Search,
  Sparkles,
  ArrowRight,
  Trash2,
  Share2
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function FavorietenScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [favoriteKoten, setFavoriteKoten] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [favoriteIDs, setFavoriteIDs] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  // Load favorites
  const loadFavorites = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const favorites = userData.favorites || [];
        setFavoriteIDs(new Set(favorites));
        
        if (favorites.length > 0) {
          // Get favorite koten details - handle case where query might have issues
          try {
            const kotenRef = collection(db, 'koten');
            const q = query(kotenRef, where('__name__', 'in', favorites.slice(0, 10)), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            
            const koten = [];
            querySnapshot.forEach((doc) => {
              const property = { id: doc.id, ...doc.data() };
              if (isTenantVisibleProperty(property)) {
                koten.push(property);
              }
            });
            
            // If we have more than 10 favorites, load the rest in batches
            if (favorites.length > 10) {
              const remainingFavorites = favorites.slice(10);
              for (const favoriteId of remainingFavorites) {
                try {
                  const kotDoc = await getDoc(doc(db, 'koten', favoriteId));
                  if (kotDoc.exists()) {
                    const property = { id: kotDoc.id, ...kotDoc.data() };
                    if (isTenantVisibleProperty(property)) {
                      koten.push(property);
                    }
                  }
                } catch (error) {
                  console.error(`Error loading property ${favoriteId}:`, error);
                }
              }
            }
            
            setFavoriteKoten(koten);
          } catch (error) {
            console.error('Error with query, trying alternative method:', error);
            // Alternative: load each property individually
            const koten = [];
            for (const favoriteId of favorites) {
              try {
                const kotDoc = await getDoc(doc(db, 'koten', favoriteId));
                if (kotDoc.exists()) {
                  const property = { id: kotDoc.id, ...kotDoc.data() };
                  if (isTenantVisibleProperty(property)) {
                    koten.push(property);
                  }
                }
              } catch (error) {
                console.error(`Error loading property ${favoriteId}:`, error);
              }
            }
            setFavoriteKoten(koten);
          }
        } else {
          setFavoriteKoten([]);
        }
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
      toast.error('Fout bij het laden van favorieten');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Toggle favorite (remove from favorites)
  const toggleFavorite = useCallback(async (kotID) => {
    if (!user) return;
    
    const newFavorites = new Set(favoriteIDs);
    newFavorites.delete(kotID);
    setFavoriteIDs(newFavorites);
    
    // Optimistic update
    setFavoriteKoten(prev => prev.filter(kot => kot.id !== kotID));
    
    try {
      await setDoc(doc(db, 'users', user.uid), {
        favorites: Array.from(newFavorites)
      }, { merge: true });
      
      toast.success('Verwijderd uit favorieten');
    } catch (error) {
      console.error('Error updating favorites:', error);
      // Revert on error
      setFavoriteIDs(favoriteIDs);
      loadFavorites();
      toast.error('Fout bij het verwijderen');
    }
  }, [user, favoriteIDs, loadFavorites]);


  // Effects
  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Filter favorites by search term
  const filteredFavorites = favoriteKoten.filter(kot =>
    kot.titel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kot.stad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kot.adres?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-[var(--market-bg)] relative overflow-hidden"
    >
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#5EABEB] via-[#4C9DE0] to-[#7F87E6]">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-white/15 blur-2xl animate-float-slow"
            style={{ 
              animationDuration: '25s',
              animationDelay: '0s'
            }}
          ></div>
          <div 
            className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-white/10 blur-2xl animate-float-slow"
            style={{ 
              animationDuration: '30s',
              animationDelay: '3s'
            }}
          ></div>
        </div>
        <div className="pointer-events-none absolute inset-0 opacity-[0.045] [background-image:radial-gradient(rgba(255,255,255,0.9)_0.7px,transparent_0.7px)] [background-size:6px_6px]" />

        <div className="relative z-10 container-professional py-10 md:py-14">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-7">
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/25 flex items-center justify-center shadow-sm">
                  <Star className="w-8 h-8 text-white fill-current" />
                </div>
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-3">
                Mijn Favorieten
              </h1>
              <p className="text-base md:text-lg text-blue-50/95">
                {favoriteKoten.length > 0 
                  ? `${favoriteKoten.length} ${favoriteKoten.length === 1 ? 'kamer' : 'kamers'} opgeslagen`
                  : 'Bewaar je favoriete kamers voor later'
                }
              </p>
            </div>

            {/* Search Bar */}
            {favoriteKoten.length > 0 && (
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Zoek in je favorieten..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-white/98 rounded-2xl border border-white/60 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#5EABEB]/45 focus:border-[#5EABEB] text-base transition-all duration-200"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-b from-transparent to-[var(--market-bg)]" />
      </div>

      {/* Content Section */}
      <div className="container-professional py-10 md:py-12">
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="relative">
                <div className="animate-spin rounded-full h-14 w-14 border-4 border-blue-200 border-t-[#5EABEB] mx-auto mb-4"></div>
                <Sparkles className="w-7 h-7 text-[#5EABEB] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
              </div>
              <div className="text-slate-600 font-medium mt-4">Favorieten laden…</div>
            </div>
          </div>
        ) : favoriteKoten.length === 0 ? (
          <div className="text-center py-20 max-w-xl mx-auto">
            <div className="w-24 h-24 bg-gradient-to-br from-[#EAF4FE] to-[#F4F9FF] rounded-full flex items-center justify-center mx-auto mb-6 border border-[#5EABEB]/20 shadow-sm">
              <Star className="w-11 h-11 text-[#5EABEB]" />
            </div>
            <h3 className="text-2xl font-semibold text-slate-900 mb-3">Geen favorieten</h3>
            <p className="text-slate-600 mb-8 max-w-md mx-auto">
              Je hebt nog geen kamers toegevoegd aan je favorieten. 
              Blader door de beschikbare kamers en voeg je favorieten toe!
            </p>
            <button
              onClick={() => navigate('/')}
              className="group inline-flex items-center justify-center px-7 py-3.5 bg-[#5EABEB] hover:bg-[#4A98D6] text-white font-semibold rounded-xl shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-[#5EABEB]/35"
            >
              <span>Bekijk kamers</span>
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        ) : filteredFavorites.length === 0 ? (
          <div className="text-center py-20 max-w-xl mx-auto">
            <div className="w-24 h-24 bg-gradient-to-br from-[#EAF4FE] to-[#F4F9FF] rounded-full flex items-center justify-center mx-auto mb-6 border border-[#5EABEB]/20 shadow-sm">
              <Search className="w-11 h-11 text-slate-400" />
            </div>
            <h3 className="text-2xl font-semibold text-slate-900 mb-3">Geen resultaten gevonden</h3>
            <p className="text-slate-600 mb-8 max-w-md mx-auto">
              Probeer andere zoektermen om je favorieten te vinden
            </p>
            <button
              onClick={() => setSearchTerm('')}
              className="px-7 py-3.5 bg-[#5EABEB] hover:bg-[#4A98D6] text-white font-semibold rounded-xl shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-[#5EABEB]/35"
            >
              Wissen
            </button>
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-6 max-w-7xl mx-auto">
              <p className="text-slate-600 text-sm md:text-base">
                {filteredFavorites.length === favoriteKoten.length 
                  ? `${filteredFavorites.length} ${filteredFavorites.length === 1 ? 'favoriet' : 'favorieten'}`
                  : `${filteredFavorites.length} van ${favoriteKoten.length} favorieten`
                }
              </p>
            </div>

            {/* Favorites Grid */}
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredFavorites.map((kot) => (
                <div 
                  key={kot.id} 
                  className="group bg-white rounded-[18px] shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-hover)] border border-[var(--surface-border)] hover:border-[#5EABEB]/35 overflow-hidden transition-all duration-200 hover:-translate-y-1"
                  onClick={() => navigate(`/property/${kot.id}`)}
                >
                  <div className="relative overflow-hidden">
                    {/* Property Image */}
                    <div className="relative h-56 overflow-hidden">
                      <PropertyImage 
                        property={kot}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        width={400}
                        height={300}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent"></div>
                    </div>

                    {/* Favorite + Share Buttons */}
                    <div className="absolute top-4 right-4 flex flex-col gap-2 z-10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(kot.id);
                        }}
                        className="p-3 bg-white/92 backdrop-blur-sm rounded-full border border-slate-200 hover:bg-white transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md group/btn focus:outline-none focus:ring-2 focus:ring-[#5EABEB]/35"
                        title="Verwijderen uit favorieten"
                      >
                        <Star 
                          className="w-5 h-5 text-yellow-500 fill-current group-hover/btn:scale-110 transition-transform" 
                        />
                      </button>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const url = `${window.location.origin}/p/${kot.id}`;
                          const title = kot.titel || 'Bekijk dit pand op StudyStay';
                          if (navigator.share) {
                            try { await navigator.share({ title, text: `${title} in ${kot.stad || ''} - €${kot.prijs || 0}/maand`, url }); } catch {}
                          } else {
                            try { await navigator.clipboard.writeText(url); toast.success('Link gekopieerd!'); } catch { toast.error('Kopiëren mislukt'); }
                          }
                        }}
                        className="p-3 bg-white/92 backdrop-blur-sm rounded-full border border-slate-200 hover:bg-white transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
                        aria-label="Pand delen"
                      >
                        <Share2 className="w-5 h-5 text-slate-400 hover:text-[#5EABEB]" />
                      </button>
                    </div>

                    {/* Price Badge */}
                    {kot.prijs && (
                      <div className="absolute bottom-4 left-4 px-4 py-2 bg-[#5EABEB] text-white rounded-xl font-semibold shadow-sm ring-1 ring-white/30">
                        €{kot.prijs}/maand
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-[#2B6FA6] transition-colors line-clamp-2">
                        {kot.titel || kot.adres || 'Naamloos pand'}
                      </h3>
                      <div className="flex items-center text-slate-600 text-sm">
                        <MapPin className="w-4 h-4 mr-1 text-[#5EABEB]" />
                        <span>{kot.stad || 'Stad niet opgegeven'}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-4 pt-4 border-t border-slate-100">
                      {kot.oppervlakte && (
                        <div className="flex items-center text-slate-600">
                          <Bed className="w-5 h-5 mr-2 text-slate-400" />
                          <span className="font-medium">{kot.oppervlakte}m²</span>
                        </div>
                      )}
                      <div className="flex items-center text-[#2B6FA6] font-semibold">
                        <span>Bekijk details</span>
                        <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(kot.id);
                      }}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 text-rose-500 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-rose-200"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Verwijderen uit favorieten</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
